import * as atlas from 'azure-maps-control';
import React, { useEffect, useState } from 'react';
import { Coordinates } from '../../../types/common/address';
import { StockLocation } from '../../../types/stock_location';

const SOURCE_ID = 'ventory_source';
const POINT_ID = 'ventory_marker';
const LAYER_ID = 'ventory_layer';
const BRUSSELS_COORDINATES = [4.3517, 50.8503];

// TODO: Update to newer map
interface ClickableMapProps {
  zoom: number;
  onClick: (coordinates: Coordinates) => void;
  stockLocation: StockLocation;
  coordinates?: Coordinates;
}

export default function ClickableMap({ coordinates, zoom, onClick, stockLocation }: ClickableMapProps) {
  const key = process.env.REACT_APP_AZURE_MAPS_SUBSCRIPTION_KEY || 'no_key';
  const [map, setMap] = useState<atlas.Map>();
  const [dataSource, setDataSource] = useState<atlas.source.DataSource>();

  const createImageSprites = async (map: atlas.Map) => {
    map.imageSprite.clear();

    if (!map.imageSprite.hasImage('marker-truck')) {
      await map.imageSprite.add(
        'marker-truck',
        `<svg id="euPi9pRCeqX1" width="28" height="28" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" project-id="b75ec37fd3ce474da9d0694dbc465375" export-id="b5634255ddb3452f818a3dd70feaeabb" cached="false">
          <ellipse rx="16" ry="16" transform="translate(16 16)" stroke-width="0"/>
          <path d="M12.8,6.625h2.10357c.22005,0,.33012,0,.43371.024176.09189.021438.17964.056788.26019.104764.09081.05411.16866.12978.32427.281111l3.65652,3.554924c.15561.151288.23346.226975.28908.315263.04941.078312.08577.163625.10782.252962C20,11.258913,20,11.365925,20,11.579862v2.482638c0,.407662,0,.611537-.06849.772363-.09135.214375-.26658.384737-.48708.47355-.16542.066588-.37512.066588-.79443.066588m-4.5,0h-1.35m0,0v-8.575c0-.980087,0-1.47014-.1962-1.844482-.17253-.329289-.44793-.597004-.7866-.764776C11.43218,4,10.92809,4,9.92,4h-5.04c-1.00809,0-1.512144,0-1.897182.190741-.338697.167773-.614061.435488-.786627.764776C2,5.32986,2,5.819913,2,6.8v6.825c0,.966525.805887,1.75,1.8,1.75m9,0h-3.6m0,0C9.2,16.824788,7.991165,18,6.5,18s-2.7-1.175213-2.7-2.625m5.4,0c0-1.449787-1.208835-2.625-2.7-2.625s-2.7,1.175213-2.7,2.625m14.85.4375C18.65,17.020613,17.64263,18,16.4,18s-2.25-.979387-2.25-2.1875s1.00737-2.1875,2.25-2.1875s2.25.979387,2.25,2.1875Z" transform="translate(5 5)" fill="none" stroke="#FFFFFF" stroke-width="1.667" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
      );
    }

    if (!map.imageSprite.hasImage('marker-building')) {
      await map.imageSprite.add(
        'marker-building',
        `<svg id="eFCDObmio6L1" width="28" height="28" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" project-id="b75ec37fd3ce474da9d0694dbc465375" export-id="e08b18a38394427ba07c798d7d6a1c28" cached="false">
          <ellipse rx="16" ry="16" transform="translate(16 16)" stroke-width="0"/>
          <path d="M6.4,6.555556h2.2M6.4,10.111111h2.2M6.4,13.666667h2.2m2.8-7.111111h2.2m-2.2,3.555555h2.2m-2.2,3.555556h2.2M16.4,19v-13.155556c0-.995644,0-1.493475-.1744-1.87376-.15336-.334515-.39816-.60648-.6992-.776915C15.18416,3,14.73608,3,13.84,3h-7.68c-.896088,0-1.344128,0-1.686384.193769-.301064.170435-.545832.4424-.699224.776915C3.6,4.350969,3.6,4.8488,3.6,5.844444L3.6,19M18,19h-16" transform="translate(6 5)" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
      );
    }
  };

  useEffect(() => {
    const newMap = new atlas.Map('map', {
      center: coordinates ? [coordinates.longitude, coordinates.latitude] : BRUSSELS_COORDINATES,
      zoom: coordinates ? zoom : 3,
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: key,
      },
    });

    newMap.events.add('ready', () => {
      createImageSprites(newMap);
      newMap.getCanvasContainer().style.cursor = 'pointer';

      const source = new atlas.source.DataSource(SOURCE_ID);
      newMap.sources.add(source);
      if (coordinates) {
        source.add(new atlas.Shape(new atlas.data.Point([coordinates.longitude, coordinates.latitude]), POINT_ID));
      }
      setDataSource(source);

      newMap.layers.add(
        new atlas.layer.SymbolLayer(source, LAYER_ID, {
          iconOptions: {
            ignorePlacement: true,
            allowOverlap: true,
            image: stockLocation.mobile ? 'marker-truck' : 'marker-building',
          },
        }),
      );

      newMap.events.add('click', function (e) {
        if (e.position) {
          onClick({ longitude: e.position[0], latitude: e.position[1] });
        }
      });
    });

    setMap(newMap);

    return () => {
      newMap.dispose();
    };
  }, []);

  useEffect(() => {
    if (!map || !dataSource) return;
    if (!coordinates) return dataSource.clear();

    map.setCamera({
      center: [coordinates.longitude, coordinates.latitude],
      type: 'ease',
      duration: 500,
    });

    dataSource.clear();
    dataSource.add(new atlas.Shape(new atlas.data.Point([coordinates.longitude, coordinates.latitude]), POINT_ID));
  }, [coordinates, map]);

  return (
    <div
      id='map'
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '4px',
      }}
    ></div>
  );
}
